/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/* eslint-disable */

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {
  Select,
  DialogActions,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Grid,
  TextField,
  Divider
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';

import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import { shapes } from '@greenville/framework';
import Roles from '../models/Roles';
import UserDialogConfig from '../../../common/config/users/UserDialogConfig';
import ValidatePermissions from '../ValidatePermissions';
import TooltipCustom from '../../../common/components/TooltipCustom';
import UsersPermissions from '../models/UsersPermissions';
import UserPrivilege from './UserPrivilege';
import { styles } from './UserStyle';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);
@inject('userPermissions')
@observer
class UserDialog extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    closeUserDialog: PropTypes.func.isRequired,
    saveUser: PropTypes.func.isRequired,
    userRoles: shapes.modelOf(Roles).isRequired,
    userPermissions: shapes.modelOf(UsersPermissions).isRequired,
    language: shapes.language.isRequired,
    showEditData: PropTypes.object.isRequired,
    updateUser: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      openDialog: true,
      userData: {
        firstName: null,
        lastName: null,
        userLogin: null,
        email: null,
        roleTypeId: null
      },
      error: false,
      editValue: {},
      openUserPrivilegeDialog: false
    };

    this.defaultSearch = {
      firstName: '',
      lastName: '',
      userLogin: '',
      email: ''
    };
  }

  componentDidMount() {
    const { userRoles, showEditData } = this.props;

    this.setState((prevState) => ({
      userData: {
        ...prevState.userData,
        roleTypeId: userRoles.usersRoles[0].roleTypeId
      }
    }));
    userRoles.usersRoles.forEach((x) => {
      if (x.role === showEditData.role) {
        this.setState((prevState) => ({
          userData: {
            ...prevState.userData,
            roleTypeId: x.roleTypeId
          }
        }));
      }
    });
  }

  handleSave = () => {
    const { saveUser, updateUser, match } = this.props;
    const { userData, editValue } = this.state;
    const { userid } = match.params;
    const trimmedUserData = {};
    Object.keys(userData).forEach((key) => {
      const value = userData[key];
      trimmedUserData[key] = _.isString(value) ? value.trim() : value;
    });
    const isValidated = this.validateSave(UserDialogConfig.requiredFields);
    if (isValidated) {
      if (userid === 'edit') {
        updateUser({ ...editValue, ...trimmedUserData }); // merge edited data
      } else {
        saveUser(trimmedUserData);
      }
    } else {
      this.setState({ error: true });
    }
  };

  static getDerivedStateFromProps(props, state) {
    const { showEditData, match } = props;
    const { editValue } = state;
    const { userid } = match.params;

    if (userid === 'edit' && showEditData && Object.keys(editValue).length === 0) {
      return {
        editValue: showEditData,
        userData: {
          ...showEditData,
          roleTypeId: showEditData.roleTypeId ?? props.userRoles.usersRoles[0]?.roleTypeId
        }
      };
    }
    return null;
  }

  validateData = (value) => {
    const { userData } = this.state;
    if (_.isEmpty(userData[value]) || _.isNil(userData[value])) {
      return true;
    }
    return false;
  };

  validateSave = (requiredArr) => {
    let bool = true;
    let required = false;
    requiredArr.forEach((value) => {
      required = this.validateData(value);
      if (required) {
        bool = false;
      }
    });
    return bool;
  };

  handleClose = () => {
    const { closeUserDialog } = this.props;
    closeUserDialog();
  };

  handleChange = (e) => {
    const { userData, editValue } = this.state;
    const { userRoles, match } = this.props;
    const { name, value } = e.target;
    const { userid } = match.params;

    // Update error state
    this.setState({ error: userData[name] === '0' });

    const intProperties = ['roleTypeId'];
    let updatedObject = {};
    let roleUpdate = {};

    if (intProperties.includes(name)) {
      const intValue = Number(value);

      // If editing a user and roleTypeId changed, update the role name
      if (userid === 'edit' && intValue !== Number(editValue.roleId)) {
        const matchedRole = userRoles.usersRoles.find((x) => Number(x.roleTypeId) === intValue);
        if (matchedRole) {
          roleUpdate = { role: matchedRole.role };
        }
      }

      updatedObject = {
        [name]: intValue,
        ...roleUpdate
      };
    } else {
      updatedObject = {
        [name]: value
      };
    }

    // Update both userData and editValue
    this.setState({
      userData: { ...userData, ...updatedObject },
      editValue: { ...editValue, ...updatedObject }
    });
  };

  handleUserPrivilege = () => {
    const { openUserPrivilegeDialog } = this.state;
    this.setState({
      openUserPrivilegeDialog: !openUserPrivilegeDialog
    });
  };

  closeUploadContent = () => {
    const { openUserPrivilegeDialog } = this.state;
    this.setState({
      openUserPrivilegeDialog: !openUserPrivilegeDialog
    });
  };

  reset = () => {
    this.setState({
      userData: this.defaultSearch
    });
  };

  render() {
    const { classes, userRoles, userPermissions, language, match } = this.props;
    const { openDialog, userData } = this.state;
    const { openUserPrivilegeDialog } = this.state;
    const { userid } = match.params;
    const permissionsList = toJS(userPermissions.permissionList.permissions);
    return (
      <>
        <ValidatePermissions givenPermissions="user_can_edit">
          <Dialog
            fullWidth="true"
            maxWidth="xs"
            open={openDialog}
            onClose={this.handleClose}
            TransitionComponent={Transition}
          >
            <AppBar className={classes.appBar} color="primary">
              <Toolbar>
                <Typography variant="h3" className={classes.title}>
                  {userid === 'add' ? 'Add New User' : 'Edit User'}
                </Typography>
                <IconButton edge="end" color="inherit" onClick={this.handleClose} aria-label="close">
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Divider className={classes.divider} />
            <Grid container className={classes.grid}>
              <Grid item xs={10}>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="h5" color="textPrimary">
                      First Name
                      <span className={classes.errorStyle}> * </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      error={this.validateData('firstName')}
                      className={classes.textFields}
                      margin="dense"
                      name="firstName"
                      id="firstName"
                      variant="outlined"
                      onChange={this.handleChange}
                      InputProps={{ classes: { input: classes.textfieldInput } }}
                      value={userData.firstName ?? ''}
                    />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="h5" color="textPrimary">
                      Last Name
                      <span className={classes.errorStyle}> * </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      className={classes.textFields}
                      error={this.validateData('lastName')}
                      name="lastName"
                      id="lastName"
                      onChange={this.handleChange}
                      margin="dense"
                      variant="outlined"
                      InputProps={{ classes: { input: classes.textfieldInput } }}
                      value={userData.lastName ?? ''}
                    />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="h5" color="textPrimary">
                      User ID
                      <span className={classes.errorStyle}> * </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      error={this.validateData('userLogin')}
                      className={classes.textFields}
                      name="userLogin"
                      id="userLogin"
                      // placeholder="User ID"
                      onChange={this.handleChange}
                      variant="outlined"
                      margin="dense"
                      InputProps={{ classes: { input: classes.textfieldInput } }}
                      value={userData.userLogin ?? ''}
                      disabled={userid === 'add' ? false : true}
                    />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="h5" color="textPrimary">
                      Email
                      <span className={classes.errorStyle}> * </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      error={this.validateData('email')}
                      className={classes.textFields}
                      name="email"
                      id="email"
                      // placeholder="Email"
                      onChange={this.handleChange}
                      variant="outlined"
                      margin="dense"
                      InputProps={{ classes: { input: classes.textfieldInput } }}
                      value={userData.email ?? ''}
                      disabled={userid === 'add' ? false : true}
                    />
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="h5" color="textPrimary">
                      Role
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Select
                      native
                      variant="outlined"
                      value={userData.roleTypeId}
                      onChange={this.handleChange}
                      // InputProps={{ classes: { input: classes.selectfieldInput } }}
                      name="roleTypeId"
                      margin="dense"
                      className={classes.selectfieldInput}
                    >
                      {userRoles.usersRoles.map((roleValue) => (
                        <option value={roleValue.roleTypeId}>{roleValue.role}</option>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <DialogActions>
              <Grid container justifyContent="center" spacing={2}>
                <Grid item>
                  <TooltipCustom
                    title={
                      !permissionsList.includes('user_can_edit') ? (
                        <FormattedMessage {...language.getText('user.PERMISSIONS_LABEL')} />
                      ) : (
                        <FormattedMessage {...language.getText('user.SAVE_USER_DETAILS')} />
                      )
                    }
                  >
                    <span>
                      <Button
                        className={classes.buttonPadding}
                        id="saveBtn"
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={this.handleSave}
                        disabled={!permissionsList.includes('user_can_edit')}
                      >
                        Save
                      </Button>
                    </span>
                  </TooltipCustom>
                </Grid>

                {userid === 'edit' && (
                  <Grid item onClick={this.reset}>
                    <TooltipCustom
                      title={
                        !permissionsList.includes('ingestion_can_edit') ? (
                          <FormattedMessage {...language.getText('user.PERMISSIONS_LABEL')} />
                        ) : (
                          <FormattedMessage {...language.getText('user.RESET')} />
                        )
                      }
                    >
                      <span>
                        <Button
                          className={classes.buttonPadding}
                          id="resetBtn"
                          variant="outlined"
                          color="primary"
                          size="small"
                        >
                          Reset
                        </Button>
                      </span>
                    </TooltipCustom>
                  </Grid>
                )}

                <Grid item>
                  <TooltipCustom
                    title={
                      <FormattedMessage
                        {...language.getText(
                          userid === 'edit' ? 'user.CANCEL_EDITING_USER' : 'user.CANCEL_ADDING_NEW_USER'
                        )}
                      />
                    }
                  >
                    <span>
                      <Button
                        id="cancelBtn"
                        className={classes.buttonPadding}
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={this.handleClose}
                      >
                        Cancel
                      </Button>
                    </span>
                  </TooltipCustom>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
          {openUserPrivilegeDialog && userid === 'edit' && (
            <UserPrivilege closeUploadContent={this.closeUploadContent} />
          )}
        </ValidatePermissions>
      </>
    );
  }
}

export default withStyles(styles)(inject('language')(observer(UserDialog)));
